/**
 * Агрегат раздела «Голос клиента» (#848): конкуренты, отзывы о продукте,
 * рыночные сигналы и потребности по разговорам окна.
 *
 * Чистая функция: SQL только отдаёт строки, а все правила счёта живут здесь,
 * под юнит-тестами, без Postgres. Единица счёта — разговор, как везде в
 * «Голосе клиента»: жалоба, повторённая в звонке пять раз, весит один.
 *
 * Свободный текст (название продукта, формулировка сигнала) не сходится —
 * 1 158 разных продуктов на 1 888 упоминаний (замер прода 28.09), поэтому
 * счёт идёт по ЗАКРЫТЫМ полям схемы анализа: уровень отзыва, тип сигнала,
 * тональность. Текст — формулировки под строкой, а не ключ группировки
 * (решение владельца 28.09, спека §3.4).
 *
 * Типы контракта — только `import type`: value-импорт `@aica/api-types` из
 * core меняет порядок цикла модулей и роняет чужие тесты edge-api. Поэтому
 * перечни уровней и типов, а также потолки приходят параметрами от маршрута,
 * который читает их из контракта: второго списка здесь нет.
 */
import type {
  VoiceCompetitor,
  VoiceCompetitorSentiment,
  VoiceFeedbackRow,
  VoiceFeedbackSeverity,
  VoiceItem,
  VoiceOfCustomerResponse,
  VoiceSignalRow,
  VoiceSignalType,
} from '@aica/api-types/schemas';
import type { GlossaryEntry } from '../schemas/analysis-client-config';
import { countCompetitorSpellings, resolveCompetitor } from './competitors';
import { rankCompetitorGroups, type CompetitorMention } from './rank-competitors';

/**
 * Один разговор окна — массивы из `businessIntelligence` и
 * `needsQualification` последнего анализа, как их отдал Postgres. Разбор
 * защитный: JSONB анализа не обязан соответствовать схеме.
 */
export interface VoiceCall {
  recordId: string;
  createdAt: string;
  competitorMentions: unknown;
  productFeedback: unknown;
  marketSignals: unknown;
  identifiedNeeds: unknown;
}

export interface VoiceAggregateOptions {
  /** Порядок строк отзывов — `VOICE_FEEDBACK_SEVERITIES` контракта. */
  severities: readonly VoiceFeedbackSeverity[];
  /** Порядок строк сигналов — `VOICE_SIGNAL_TYPES` контракта. */
  signalTypes: readonly VoiceSignalType[];
  sentiments: readonly VoiceCompetitorSentiment[];
  itemsPerRow: number;
  competitorsLimit: number;
  /**
   * The current window's scan hit its cap: its oldest conversations were not
   * read. A competitor missing from the scanned part may still have been named
   * this period, so no "named only before" rows are made (#1379).
   */
  currentTruncated?: boolean;
}

export type VoiceAggregate = Pick<VoiceOfCustomerResponse, 'feedback' | 'signals'> & {
  competitors: Omit<VoiceOfCustomerResponse['competitors'], 'mergeUnavailable'>;
  /**
   * Distinct competitors named in the current window — `competitors.rows` is
   * cut to `competitorsLimit`, so a shorter list says «and K more» from this.
   */
  competitorsInWindow: number;
  /** The needs' own N is counted by the caller, over its own population. */
  needs: Omit<VoiceOfCustomerResponse['needs'], 'callsInWindow' | 'prevCallsInWindow'>;
};

function objects(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is Record<string, unknown> =>
      !!item && typeof item === 'object' && !Array.isArray(item)
  );
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/**
 * Строка по закрытому полю: разговоры, где значение встретилось, и по одной
 * формулировке с каждого — свежие первыми (вызывающий подаёт разговоры в
 * таком порядке). Разговор без текста считается, но не раскрывается.
 */
function tallyClosedField<K extends string, I extends VoiceItem>(
  keys: readonly K[],
  calls: readonly VoiceCall[],
  itemsOf: (call: VoiceCall) => { key: K; item: I | null }[],
  itemsPerRow: number
): Map<K, { calls: number; items: I[] }> {
  const rows = new Map<K, { calls: number; items: I[] }>(
    keys.map((key) => [key, { calls: 0, items: [] }])
  );
  for (const call of calls) {
    const seen = new Map<K, I | null>();
    for (const { key, item } of itemsOf(call)) {
      // Первая формулировка разговора с текстом; ключ учтён и без него.
      if (!seen.has(key) || (seen.get(key) === null && item !== null)) {
        seen.set(key, item);
      }
    }
    for (const [key, item] of seen) {
      const row = rows.get(key)!;
      row.calls += 1;
      if (item && row.items.length < itemsPerRow) row.items.push(item);
    }
  }
  return rows;
}

function feedbackOf(severities: readonly VoiceFeedbackSeverity[]) {
  return (call: VoiceCall) =>
    objects(call.productFeedback).flatMap((entry) => {
      const key = oneOf(entry.severity, severities);
      if (!key) return [];
      const feedback = text(entry.feedback);
      return [
        {
          key,
          item: feedback
            ? {
                recordId: call.recordId,
                createdAt: call.createdAt,
                text: feedback,
                product: text(entry.product),
              }
            : null,
        },
      ];
    });
}

function signalsOf(types: readonly VoiceSignalType[]) {
  return (call: VoiceCall) =>
    objects(call.marketSignals).flatMap((entry) => {
      const key = oneOf(entry.type, types);
      if (!key) return [];
      const signal = text(entry.signal);
      return [
        {
          key,
          item: signal
            ? { recordId: call.recordId, createdAt: call.createdAt, text: signal }
            : null,
        },
      ];
    });
}

function needsOf(call: VoiceCall): { key: 'needs'; item: VoiceItem | null }[] {
  if (!Array.isArray(call.identifiedNeeds)) return [];
  return call.identifiedNeeds.flatMap((need) => {
    const wording = text(need);
    return wording
      ? [
          {
            key: 'needs' as const,
            item: { recordId: call.recordId, createdAt: call.createdAt, text: wording },
          },
        ]
      : [];
  });
}

interface Mention {
  recordId: string;
  name: unknown;
  context: string | null;
  sentiment: VoiceCompetitorSentiment | null;
  createdAt: string;
}

function mentionsOf(
  calls: readonly VoiceCall[],
  sentiments: readonly VoiceCompetitorSentiment[]
): Mention[] {
  return calls.flatMap((call) =>
    objects(call.competitorMentions).map((entry) => ({
      recordId: call.recordId,
      name: entry.name,
      context: text(entry.context),
      sentiment: oneOf(entry.sentiment, sentiments),
      createdAt: call.createdAt,
    }))
  );
}

/**
 * Тональность и формулировки строки конкурента — из упоминаний скана: в
 * фактах их нет. Тональность — разговоры с ней, формулировка — по одной с
 * разговора (первая с текстом), свежие первыми.
 */
function competitorPresentation(
  mentions: readonly Mention[],
  options: VoiceAggregateOptions
): Pick<VoiceCompetitor, 'sentiment' | 'items'> {
  const sentimentCalls = new Map<VoiceCompetitorSentiment, Set<string>>(
    options.sentiments.map((s) => [s, new Set<string>()])
  );
  const itemByCall = new Map<string, VoiceCompetitor['items'][number] | null>();
  for (const mention of mentions) {
    if (mention.sentiment) sentimentCalls.get(mention.sentiment)!.add(mention.recordId);
    const item = mention.context
      ? {
          recordId: mention.recordId,
          createdAt: mention.createdAt,
          text: mention.context,
          sentiment: mention.sentiment,
        }
      : null;
    if (
      !itemByCall.has(mention.recordId) ||
      (!itemByCall.get(mention.recordId) && item)
    ) {
      itemByCall.set(mention.recordId, item);
    }
  }
  return {
    sentiment: {
      positive: sentimentCalls.get('positive')?.size ?? 0,
      neutral: sentimentCalls.get('neutral')?.size ?? 0,
      negative: sentimentCalls.get('negative')?.size ?? 0,
    },
    items: [...itemByCall.values()]
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .slice(0, options.itemsPerRow),
  };
}

/**
 * Конкуренты тем же `rankCompetitorGroups`, что и «Головне»: одни пары
 * (разговор, имя) дают одни числа на одном окне (#838). Прошлое окно сводится
 * тем же индексом глоссария, иначе сдвиг сравнивал бы разные имена.
 *
 * Конкурент, которого называли в прошлом окне, а в этом нет, — тоже строка:
 * ноль разговоров и сдвиг «было N %, стало 0», как у уровней отзывов и
 * сигналов (#1379). Иначе он молча выпадал бы, и «о нём перестали говорить»
 * нельзя было бы увидеть. Такие строки идут после строк текущего окна, по
 * убыванию прошлого числа, не больше `competitorsLimit`. Ключ сведения зависит
 * только от глоссария и написания, поэтому прошлое окно группируется тем же
 * правилом, а не вторым.
 */
function competitorRows(
  current: readonly VoiceCall[],
  previous: readonly VoiceCall[],
  glossary: readonly GlossaryEntry[] | null | undefined,
  options: VoiceAggregateOptions
): Pick<VoiceAggregate, 'competitors' | 'competitorsInWindow'> {
  // Each window's mentions are built once and reused below.
  const currentMentions = mentionsOf(current, options.sentiments);
  const previousMentions = mentionsOf(previous, options.sentiments);
  const ranked = rankCompetitorGroups(
    currentMentions,
    glossary,
    options.competitorsLimit
  );

  const prevCallsByKey = new Map<string, Set<string>>();
  for (const mention of previousMentions) {
    const resolved = resolveCompetitor(mention.name, ranked.index);
    if (!resolved) continue;
    const calls = prevCallsByKey.get(resolved.key) ?? new Set<string>();
    calls.add(mention.recordId);
    prevCallsByKey.set(resolved.key, calls);
  }

  const rows: VoiceCompetitor[] = ranked.groups.map((group) => ({
    ...group.competitor,
    key: group.key,
    prevCalls: prevCallsByKey.get(group.key)?.size ?? 0,
    ...competitorPresentation(group.mentions, options),
  }));

  if (options.currentTruncated) {
    return {
      competitors: {
        rows,
        unmergedShare: ranked.unmergedShare,
        unmergedExamples: ranked.unmergedExamples,
      },
      competitorsInWindow: ranked.total,
    };
  }
  const currentKeys = new Set<string>();
  for (const mention of currentMentions) {
    const resolved = resolveCompetitor(mention.name, ranked.index);
    if (resolved) currentKeys.add(resolved.key);
  }
  // Ranked by the same rule as the current window (same glossary, so the same
  // keys); only the groups absent now are kept.
  const dropped: VoiceCompetitor[] = rankCompetitorGroups(
    previousMentions,
    glossary,
    options.competitorsLimit + currentKeys.size
  )
    .groups.filter((group) => !currentKeys.has(group.key))
    .slice(0, options.competitorsLimit)
    .map((group) => ({
      name: group.competitor.name,
      merged: group.competitor.merged,
      key: group.key,
      calls: 0,
      prevCalls: prevCallsByKey.get(group.key)?.size ?? group.competitor.calls,
      sentiment: { positive: 0, neutral: 0, negative: 0 },
      items: [],
    }));

  return {
    competitors: {
      rows: [...rows, ...dropped],
      unmergedShare: ranked.unmergedShare,
      unmergedExamples: ranked.unmergedExamples,
    },
    competitorsInWindow: ranked.total,
  };
}

/**
 * Fewer than `threshold` conversations, but not none: «a guess, not a
 * pattern». One rule for the screen, the exports and the email; the threshold
 * is the contract's `VOICE_SMALL_SAMPLE_CALLS`, passed in because core reads
 * api-types by type only.
 */
export function isVoiceSmallSample(calls: number, threshold: number): boolean {
  return calls > 0 && calls < threshold;
}

/**
 * `current` и `previous` — разговоры текущего и прошлого окна, свежие первыми.
 * У прошлого окна тексты не нужны и могут отсутствовать: из него берутся
 * только счётчики.
 */
export function aggregateVoiceOfCustomer(
  current: readonly VoiceCall[],
  previous: readonly VoiceCall[],
  glossary: readonly GlossaryEntry[] | null | undefined,
  options: VoiceAggregateOptions
): VoiceAggregate {
  const feedbackNow = tallyClosedField(
    options.severities,
    current,
    feedbackOf(options.severities),
    options.itemsPerRow
  );
  const feedbackBefore = tallyClosedField(
    options.severities,
    previous,
    feedbackOf(options.severities),
    0
  );
  const signalsNow = tallyClosedField(
    options.signalTypes,
    current,
    signalsOf(options.signalTypes),
    options.itemsPerRow
  );
  const signalsBefore = tallyClosedField(
    options.signalTypes,
    previous,
    signalsOf(options.signalTypes),
    0
  );
  const needsNow = tallyClosedField(['needs'], current, needsOf, options.itemsPerRow);
  const needsBefore = tallyClosedField(['needs'], previous, needsOf, 0);

  const feedback: VoiceFeedbackRow[] = options.severities.map((severity) => ({
    severity,
    calls: feedbackNow.get(severity)!.calls,
    prevCalls: feedbackBefore.get(severity)!.calls,
    items: feedbackNow.get(severity)!.items,
  }));
  const signals: VoiceSignalRow[] = options.signalTypes.map((type) => ({
    type,
    calls: signalsNow.get(type)!.calls,
    prevCalls: signalsBefore.get(type)!.calls,
    items: signalsNow.get(type)!.items,
  }));
  const needs: VoiceAggregate['needs'] = {
    calls: needsNow.get('needs')!.calls,
    prevCalls: needsBefore.get('needs')!.calls,
    items: needsNow.get('needs')!.items,
  };

  return {
    ...competitorRows(current, previous, glossary, options),
    feedback,
    signals,
    needs,
  };
}

/**
 * Счётчики одного окна из фактов «Голоса клиента» (#2394 §9 п. 5,
 * docs/plans/2026-10-18-voc-trends-read.md §2): K строк и N блока
 * бизнес-аналитики, по всему окну — без потолка скана.
 */
export interface VoiceFactWindow {
  /** K по уровню отзыва. */
  feedback: ReadonlyMap<string, number>;
  /** K по типу сигнала. */
  signals: ReadonlyMap<string, number>;
  /** K по сведённому ключу конкурента (`resolved_key`). */
  competitors: ReadonlyMap<string, number>;
}

export interface VoiceFactsAggregateInput {
  current: VoiceFactWindow;
  previous: VoiceFactWindow;
  /**
   * Разговоры с потребностью — счёт по всему окну, как K фактов: скан с
   * потолком дал бы нижнюю границу. Строки — из скана текущего окна.
   */
  needsCalls: number;
  /** То же за прошлое окно — счёт, без строк. */
  prevNeedsCalls: number;
  /**
   * Упоминания (разговор, имя) у показанных ключей, которых нет в скане:
   * строки «называли только раньше» (прошлое окно) и — при потолке скана —
   * строки текущего окна. Из них только написание и пометка `merged`.
   */
  nameMentions: readonly CompetitorMention[];
  /** Ключ сведения → ключ фактов: писатель режет ключ до 200 кодовых точек. */
  factKey: (key: string) => string;
}

/** Ключи по убыванию K, при равенстве — по коду символа (порядок `rankSignals`). */
function byCalls(counts: ReadonlyMap<string, number>): [string, number][] {
  return [...counts]
    .filter(([, calls]) => calls > 0)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
}

/**
 * Тот же ответ, что `aggregateVoiceOfCustomer`, но числа строк — из фактов
 * (потребности — счётом по окну, их в фактах нет), а скан текущего окна даёт
 * только подачу: формулировки, тональность и написание конкурента, долю
 * несведённых имён. Порядок и срез конкурентов — по K фактов тем же правилом, что у
 * `rankSignals`; строки «называли только раньше» — тоже по фактам, и при
 * потолке скана тоже: K фактов от потолка не зависит.
 *
 * `plan` — `planVoiceFactsCompetitors` тех же `current`, окон и глоссария;
 * вызывающий, которому план нужен ещё и для `voiceFactsKeysWithoutNames`,
 * строит его один раз. Нет — строится здесь.
 */
export function aggregateVoiceOfCustomerFromFacts(
  current: readonly VoiceCall[],
  facts: VoiceFactsAggregateInput,
  glossary: readonly GlossaryEntry[] | null | undefined,
  options: VoiceAggregateOptions,
  plan: VoiceFactsCompetitorPlan = planVoiceFactsCompetitors(
    current,
    facts,
    glossary,
    options
  )
): VoiceAggregate {
  const feedbackNow = tallyClosedField(
    options.severities,
    current,
    feedbackOf(options.severities),
    options.itemsPerRow
  );
  const signalsNow = tallyClosedField(
    options.signalTypes,
    current,
    signalsOf(options.signalTypes),
    options.itemsPerRow
  );
  const needsNow = tallyClosedField(['needs'], current, needsOf, options.itemsPerRow);
  // A conversation the scan saw but the facts lack (coverage is ≥ 99 %, not
  // 100 %) must not put a wording under a zero.
  const itemsUnder = <I>(calls: number, items: I[]): I[] => (calls > 0 ? items : []);

  const feedback: VoiceFeedbackRow[] = options.severities.map((severity) => {
    const calls = facts.current.feedback.get(severity) ?? 0;
    return {
      severity,
      calls,
      prevCalls: facts.previous.feedback.get(severity) ?? 0,
      items: itemsUnder(calls, feedbackNow.get(severity)!.items),
    };
  });
  const signals: VoiceSignalRow[] = options.signalTypes.map((type) => {
    const calls = facts.current.signals.get(type) ?? 0;
    return {
      type,
      calls,
      prevCalls: facts.previous.signals.get(type) ?? 0,
      items: itemsUnder(calls, signalsNow.get(type)!.items),
    };
  });

  return {
    ...competitorRowsFromFacts(facts, glossary, options, plan),
    feedback,
    signals,
    needs: {
      calls: facts.needsCalls,
      prevCalls: facts.prevNeedsCalls,
      items: itemsUnder(facts.needsCalls, needsNow.get('needs')!.items),
    },
  };
}

const EVERYTHING = Number.MAX_SAFE_INTEGER;

/**
 * Строки конкурентов на фактах: показанные ключи (топ текущего окна по K и
 * «называли только раньше») и группы скана текущего окна по ключу фактов.
 */
export function planVoiceFactsCompetitors(
  current: readonly VoiceCall[],
  facts: Omit<VoiceFactsAggregateInput, 'nameMentions'>,
  glossary: readonly GlossaryEntry[] | null | undefined,
  options: VoiceAggregateOptions
) {
  const top = byCalls(facts.current.competitors).slice(0, options.competitorsLimit);
  const dropped = byCalls(facts.previous.competitors)
    .filter(([key]) => !facts.current.competitors.has(key))
    .slice(0, options.competitorsLimit);
  const shown = new Set(top.map(([key]) => key));
  const mentions = mentionsOf(current, options.sentiments);
  const scan = rankCompetitorGroups(mentions, glossary, EVERYTHING, (key) =>
    shown.has(facts.factKey(key))
  );
  // Two names that differ only past the cut are one fact key: one row, and
  // the presentation of both groups' mentions — in the scan's own order
  // (freshest conversation first), not group after group.
  const position = new Map<Mention, number>(mentions.map((mention, i) => [mention, i]));
  const scanned = new Map<string, (typeof scan.groups)[number]>();
  for (const group of scan.groups) {
    const key = facts.factKey(group.key);
    const seen = scanned.get(key);
    scanned.set(
      key,
      seen
        ? {
            ...seen,
            mentions: [...seen.mentions, ...group.mentions].sort(
              (a, b) => position.get(a)! - position.get(b)!
            ),
          }
        : group
    );
  }
  return { top, dropped, scan, scanned };
}

export type VoiceFactsCompetitorPlan = ReturnType<typeof planVoiceFactsCompetitors>;

/**
 * Ключи, чьё написание надо дочитать (`nameMentions`): показанные строки
 * текущего окна, которых нет в скане (он упёрся в потолок), и строки
 * «называли только раньше» — по своему окну каждая.
 */
export function voiceFactsKeysWithoutNames(plan: VoiceFactsCompetitorPlan): {
  current: string[];
  previous: string[];
} {
  return {
    current: plan.top.map(([key]) => key).filter((key) => !plan.scanned.has(key)),
    previous: plan.dropped.map(([key]) => key),
  };
}

function competitorRowsFromFacts(
  facts: VoiceFactsAggregateInput,
  glossary: readonly GlossaryEntry[] | null | undefined,
  options: VoiceAggregateOptions,
  plan: VoiceFactsCompetitorPlan
): Pick<VoiceAggregate, 'competitors' | 'competitorsInWindow'> {
  const { top, dropped: droppedKeys, scan, scanned } = plan;
  const named = new Map<string, Pick<VoiceCompetitor, 'name' | 'merged'>>();
  for (const group of rankCompetitorGroups(facts.nameMentions, glossary, EVERYTHING)
    .groups) {
    const key = facts.factKey(group.key);
    if (!named.has(key)) named.set(key, group.competitor);
  }
  // The scan's own rule (`rankCompetitorGroups`): a row is merged when its key
  // is a glossary term, and always when there is no glossary to merge into.
  const hasGlossary = countCompetitorSpellings(scan.index) > 0;
  const identity = (key: string): Pick<VoiceCompetitor, 'name' | 'merged'> => {
    const found = scanned.get(key)?.competitor ?? named.get(key);
    return found
      ? { name: found.name, merged: found.merged }
      : {
          name: key,
          merged: hasGlossary
            ? resolveCompetitor(key, scan.index)?.mapped === true
            : true,
        };
  };

  const rows: VoiceCompetitor[] = top.map(([key, calls]) => {
    const group = scanned.get(key);
    return {
      ...identity(key),
      key,
      calls,
      prevCalls: facts.previous.competitors.get(key) ?? 0,
      ...(group
        ? competitorPresentation(group.mentions, options)
        : { sentiment: { positive: 0, neutral: 0, negative: 0 }, items: [] }),
    };
  });
  const dropped: VoiceCompetitor[] = droppedKeys.map(([key, prevCalls]) => ({
    ...identity(key),
    key,
    calls: 0,
    prevCalls,
    sentiment: { positive: 0, neutral: 0, negative: 0 },
    items: [],
  }));

  return {
    competitors: {
      rows: [...rows, ...dropped],
      unmergedShare: scan.unmergedShare,
      unmergedExamples: scan.unmergedExamples,
    },
    competitorsInWindow: byCalls(facts.current.competitors).length,
  };
}
